/**
 * NOS-132 M14: Ticket types list with drag reorder, CRUD, paid field disabled.
 */

import { useCallback, useEffect, useMemo, useState } from "react"
import debounce from "lodash.debounce"
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCenter,
  type DragEndEvent,
} from "@dnd-kit/core"
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { GripVertical, Lock, Plus } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Switch } from "@/components/ui/switch"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Loader2 } from "lucide-react"
import {
  useTicketTypes,
  type EventTicketTypeRow,
  type SaveTicketTypeInput,
} from "@/hooks/useTicketTypes"

function SortableTicketRow({
  id,
  children,
}: {
  id: string
  children: React.ReactNode
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex gap-2 rounded-lg border bg-card p-3 ${isDragging ? "opacity-60 shadow-md" : ""}`}
    >
      <button
        type="button"
        className="touch-none cursor-grab self-start p-1 text-muted-foreground active:cursor-grabbing"
        {...attributes}
        {...listeners}
        aria-label="Drag to reorder ticket type"
      >
        <GripVertical className="h-4 w-4" />
      </button>
      <div className="min-w-0 flex-1 space-y-3">{children}</div>
    </div>
  )
}

export interface TicketTypesEditorProps {
  eventId: string
  orgId: string
  disabled?: boolean
}

export function TicketTypesEditor({ eventId, orgId, disabled }: TicketTypesEditorProps) {
  const {
    ticketTypes,
    loading,
    saveTicketType,
    deleteTicketType,
    reorderTicketTypes,
    countRegistrationsForTicketType,
  } = useTicketTypes(disabled ? null : eventId, disabled ? null : orgId)

  const [pendingDelete, setPendingDelete] = useState<EventTicketTypeRow | null>(null)
  const [deleteRegCount, setDeleteRegCount] = useState(0)

  const openDeleteConfirm = async (row: EventTicketTypeRow) => {
    const n = await countRegistrationsForTicketType(row.id)
    setDeleteRegCount(n)
    setPendingDelete(row)
  }

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return
    await deleteTicketType(pendingDelete.id)
    setPendingDelete(null)
  }

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const sorted = useMemo(
    () => [...ticketTypes].sort((a, b) => a.sort_order - b.sort_order),
    [ticketTypes]
  )

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = sorted.findIndex((t) => t.id === active.id)
    const newIndex = sorted.findIndex((t) => t.id === over.id)
    if (oldIndex < 0 || newIndex < 0) return
    const next = arrayMove(sorted, oldIndex, newIndex)
    await reorderTicketTypes(next.map((t) => t.id))
  }

  const handleAdd = async () => {
    const nextOrder = sorted.length ? Math.max(...sorted.map((t) => t.sort_order)) + 1 : 0
    await saveTicketType({
      event_id: eventId,
      org_id: orgId,
      name: "New ticket",
      description: null,
      price_cents: 0,
      capacity: null,
      seats_per_ticket: 1,
      sort_order: nextOrder,
      is_active: true,
    })
  }

  if (disabled) {
    return (
      <p className="text-sm text-muted-foreground">
        Save the event first to configure ticket types.
      </p>
    )
  }

  if (loading) {
    return (
      <div className="flex justify-center py-6">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={sorted.map((t) => t.id)} strategy={verticalListSortingStrategy}>
          <div className="space-y-3">
            {sorted.map((row) => (
              <TicketTypeRowEditor
                key={row.id}
                row={row}
                eventId={eventId}
                orgId={orgId}
                saveTicketType={saveTicketType}
                onRequestDelete={() => void openDeleteConfirm(row)}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      <Button type="button" variant="outline" size="sm" onClick={() => void handleAdd()} className="w-full sm:w-auto">
        <Plus className="mr-2 h-4 w-4" />
        Add ticket type
      </Button>

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete ticket type?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteRegCount > 0
                ? `This ticket type has ${deleteRegCount} active registration(s). Deleting it may affect reporting. This cannot be undone.`
                : "This cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => void handleConfirmDelete()}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

interface TicketTypeRowEditorProps {
  row: EventTicketTypeRow
  eventId: string
  orgId: string
  saveTicketType: (input: SaveTicketTypeInput) => Promise<EventTicketTypeRow | null>
  onRequestDelete: () => void
}

function TicketTypeRowEditor({
  row,
  eventId,
  orgId,
  saveTicketType,
  onRequestDelete,
}: TicketTypeRowEditorProps) {
  const [name, setName] = useState(row.name)
  const [description, setDescription] = useState(row.description ?? "")
  const [capacity, setCapacity] = useState(row.capacity != null ? String(row.capacity) : "")
  const [seats, setSeats] = useState(String(row.seats_per_ticket))
  const [active, setActive] = useState(row.is_active)

  useEffect(() => {
    setName(row.name)
    setDescription(row.description ?? "")
    setCapacity(row.capacity != null ? String(row.capacity) : "")
    setSeats(String(row.seats_per_ticket))
    setActive(row.is_active)
  }, [row.id, row.name, row.description, row.capacity, row.seats_per_ticket, row.is_active])

  const flushSave = useCallback(
    async (patch: {
      name?: string
      description?: string | null
      capacity?: number | null
      seats_per_ticket?: number
      is_active?: boolean
    }) => {
      await saveTicketType({
        id: row.id,
        event_id: eventId,
        org_id: orgId,
        name: patch.name ?? name,
        description:
          patch.description !== undefined ? patch.description : (description || null),
        price_cents: row.price_cents,
        capacity:
          patch.capacity !== undefined
            ? patch.capacity
            : capacity === ""
              ? null
              : parseInt(capacity, 10) || null,
        seats_per_ticket: patch.seats_per_ticket ?? (parseInt(seats, 10) || 1),
        sort_order: row.sort_order,
        is_active: patch.is_active ?? active,
      })
    },
    [row, eventId, orgId, name, description, capacity, seats, active, saveTicketType]
  )

  const debouncedName = useMemo(
    () =>
      debounce((next: string) => {
        void flushSave({ name: next })
      }, 600),
    [flushSave]
  )

  useEffect(
    () => () => {
      debouncedName.cancel()
    },
    [debouncedName]
  )

  return (
    <SortableTicketRow id={row.id}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs font-medium text-muted-foreground">Ticket type</span>
        <div className="flex items-center gap-2">
          <Switch
            checked={active}
            onCheckedChange={(v) => {
              setActive(v)
              void flushSave({ is_active: v })
            }}
            id={`tt-active-${row.id}`}
          />
          <Label htmlFor={`tt-active-${row.id}`} className="text-xs font-normal">
            Active
          </Label>
        </div>
      </div>
      <div className="space-y-2">
        <Label className="text-xs">Name</Label>
        <Input
          value={name}
          onChange={(e) => {
            const v = e.target.value
            setName(v)
            debouncedName(v)
          }}
          onBlur={() => {
            debouncedName.cancel()
            void flushSave({ name })
          }}
        />
      </div>
      <div className="space-y-2">
        <Label className="text-xs">Description (optional)</Label>
        <Input
          value={description}
          onChange={(e) => {
            setDescription(e.target.value)
          }}
          onBlur={() => {
            void flushSave({ description: description || null })
          }}
        />
      </div>
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Label className="text-xs">Price (cents)</Label>
          <span title="Paid ticketing coming in a future update">
            <Lock className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          </span>
          <span className="text-xs text-muted-foreground">Free — Paid ticketing coming soon</span>
        </div>
        <Input
          type="number"
          min={0}
          value={row.price_cents}
          disabled
          className="bg-muted"
          readOnly
          aria-label="Ticket price (paid ticketing coming soon)"
        />
      </div>
      <div className="space-y-2">
        <Label className="text-xs">Per-type capacity (optional)</Label>
        <Input
          type="number"
          min={0}
          value={capacity}
          onChange={(e) => setCapacity(e.target.value)}
          onBlur={() =>
            flushSave({
              capacity: capacity === "" ? null : parseInt(capacity, 10) || null,
            })
          }
          placeholder="Unlimited"
        />
      </div>
      <details className="rounded-md border bg-muted/20 px-3 py-2 text-sm">
        <summary className="cursor-pointer font-medium text-muted-foreground">Advanced</summary>
        <div className="mt-3 space-y-2">
          <Label className="text-xs">Seats per ticket</Label>
          <Input
            type="number"
            min={1}
            value={seats}
            onChange={(e) => setSeats(e.target.value)}
            onBlur={() => flushSave({ seats_per_ticket: parseInt(seats, 10) || 1 })}
          />
        </div>
      </details>
      <Button type="button" variant="ghost" size="sm" className="text-destructive" onClick={onRequestDelete}>
        Delete
      </Button>
    </SortableTicketRow>
  )
}
